import { Link } from 'react-router-dom';
import {
  UXP_MC_ID,
  DASHBOARD_METRICS,
  BRIEFING_TYPES,
  NOTIFICATION_CATEGORIES,
  APPROVAL_TYPES,
  COMPANION_CAPABILITIES,
  VOICE_FEATURES,
  CRISIS_TYPES,
  OFFLINE_FEATURES,
  SECURITY_FEATURES,
  MOBILE_ANALYTICS,
  COLLABORATION_FEATURES,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_BRIEFINGS,
  DEMO_NOTIFICATIONS,
  DEMO_APPROVALS,
  DEMO_COMPANION,
  DEMO_VOICE,
  DEMO_CRISIS,
  DEMO_OFFLINE,
  buildMobileCompanionDashboard,
} from '../../mobilecompanion/framework';
import {
  ExecutiveMobileDashboardPanel,
  MobileAIBriefingsPanel,
  NotificationCenterPanel,
  ExecutiveApprovalsPanel,
  AICompanionPanel,
  VoiceExperiencePanel,
  CrisisCenterPanel,
  OfflineExperiencePanel,
} from '../../components/mobilecompanion';
import { Card, KpiCard } from '../../components/ui';

export function MobileCompanionPage() {
  const dashboard = buildMobileCompanionDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_MC_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Mobile Companion</h1>
        <p className="mt-1 text-sm text-slate-500">
          Mobile Companion, Executive Anywhere & Intelligent Notification Operating System —
          executive decision-making from anywhere with AI guidance and secure approvals.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-100 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Dashboard Metrics" value={dashboard.dashboard_metrics.toString()} />
        <KpiCard label="Briefing Types" value={dashboard.briefing_types.toString()} />
        <KpiCard label="Notification Types" value={dashboard.notification_types.toString()} />
        <KpiCard label="Voice Features" value={dashboard.voice_features.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Executive Mobile Dashboard</h2>
        <p className="mt-1 text-sm text-slate-500">{DASHBOARD_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ExecutiveMobileDashboardPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">AI Briefings</h2>
          <p className="mt-1 text-sm text-slate-500">{BRIEFING_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <MobileAIBriefingsPanel briefings={DEMO_BRIEFINGS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Notification Center</h2>
          <p className="mt-1 text-sm text-slate-500">{NOTIFICATION_CATEGORIES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <NotificationCenterPanel notifications={DEMO_NOTIFICATIONS} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Executive Approvals</h2>
        <p className="mt-1 text-sm text-slate-500">{APPROVAL_TYPES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ExecutiveApprovalsPanel approvals={DEMO_APPROVALS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">AI Companion</h2>
          <p className="mt-1 text-sm text-slate-500">{COMPANION_CAPABILITIES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <AICompanionPanel sessions={DEMO_COMPANION} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Voice Experience</h2>
          <p className="mt-1 text-sm text-slate-500">{VOICE_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <VoiceExperiencePanel commands={DEMO_VOICE} />
          </Card>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Crisis Center</h2>
          <p className="mt-1 text-sm text-slate-500">{CRISIS_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <CrisisCenterPanel events={DEMO_CRISIS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Offline Experience</h2>
          <p className="mt-1 text-sm text-slate-500">{OFFLINE_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <OfflineExperiencePanel items={DEMO_OFFLINE} />
          </Card>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Security</h2>
          <p className="mt-2 text-xs text-slate-500">{SECURITY_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Mobile Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{MOBILE_ANALYTICS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Collaboration</h2>
          <p className="mt-2 text-xs text-slate-500">{COLLABORATION_FEATURES.join(' · ')}</p>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Accessibility</h2>
        <div className="mt-2 flex flex-wrap gap-1">
          {ACCESSIBILITY_STANDARDS.map((s) => (
            <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
